/**
 * @file  vehicle/index.ts
 * @description  vehicle service to manage all the vehicle related operations
 *
 * @author dlima (dlima-electron)
 *
 */
/**
 * Imports.
 */
// Modules
import { isEmpty, isNil, unescape } from 'lodash';
// Services
import * as CustomerModels from '../../functions/customer/customer.model';
import * as QuotesModels from '../../functions/quotes/quotes.model';
import * as VehicleModels from '../../functions/vehicle/vehicle.model';
import * as _cache from '../cache';
import * as _log from '../logger';
// Components
import { SelectItem } from '../../elements/Select/styles';
// Models
import { VehicleData } from './models';
// Data
import { ContractDetails } from '../../functions/contract/contract.model';
import { getVinDetails } from '../../functions/vehicle';
import { getCurrentContract } from '../contract';
import { getCustomer } from '../customer';
import { isVinApiEnabled } from '../settings';
import { getPreRatingOptions } from '../surcharges';
import data from './data.json';

/**
 * Enumerates the cache keys.
 */
export enum Category {
  car = 'car'
}

/**
 * get sample data from json file
 */
const sample = (): VehicleData => {
  return data;
};

/**
 * get list of 20 years (last 18 + current + next)
 * @returns list of SelectItem
 */
export const getYears = (): SelectItem[] => {
  const selectItems: SelectItem[] = [];

  selectItems.push({
    text: 'Select Year',
    value: ''
  });

  const lastYear = new Date().getFullYear();
  for (let i = 0; i <= 20; i++) {
    selectItems.push({
      value: (lastYear - i).toString(),
      text: (lastYear - i).toString()
    });
  }

  return selectItems;
};

/**
 * get makes by year
 * @param year year information
 * @returns list of SelectItem
 */
export const getMakeByYear = (year: number): SelectItem[] => {
  const selectItems: SelectItem[] = [];

  selectItems.push({
    text: 'Select Make',
    value: ''
  });

  const data = sample();
  if (!data) {
    return selectItems;
  }

  const makesObj = data[year];
  if (!makesObj) {
    return selectItems;
  }

  const makeList = Object.keys(makesObj);
  if (!makeList) {
    return selectItems;
  }

  makeList.sort().forEach((make) => {
    selectItems.push({ value: unescape(make), text: unescape(make) });
  });

  return selectItems;
};

/**
 * get models by year and make
 * @param year year information
 * @param make make information
 * @returns list of SelectItem
 */
export const getModelsByYearAndMake = (
  year: number | undefined,
  make: string
): SelectItem[] => {
  const selectItems: SelectItem[] = [];

  selectItems.push({
    text: 'Select Model',
    value: ''
  });

  const data = sample();
  if (!data || year === undefined) {
    return selectItems;
  }

  const makesObj = data[year];
  if (!makesObj) {
    return selectItems;
  }

  const modelsObj = makesObj[make];
  if (!modelsObj) {
    return selectItems;
  }

  const modelsList = Object.values(modelsObj);
  if (!modelsList) {
    return selectItems;
  }

  modelsList.sort().forEach((model) => {
    selectItems.push({ value: unescape(model), text: unescape(model) });
  });

  return selectItems;
};

/**
 * verify if vin is valid
 * @param vin vin information
 * @returns boolean flag which indicates if vin is valid
 */
export const isValidVin = (vin: string): boolean => {
  const regexVin = /[A-HJ-NPR-Za-hj-npr-z0-9]{17,17}/g;

  return Boolean(vin) && regexVin.test(vin as string) && isNaN(Number(vin));
};

/**
 * Gets vehicle info.
 * @returns Vehicle data.
 */
export const getVehicle = (): VehicleModels.default => {
  let car = _cache.getItem<VehicleModels.default>(_cache.Key.vehicle);

  if (!car) {
    const customer = _cache.getItem<CustomerModels.Customer>(
      _cache.Key.contact
    );
    car = customer?.cars?.find(
      (car) => car.vin === customer.vin
    ) as VehicleModels.default;
    if (car) {
      car.zip = customer?.homeAddress?.zip;
      car.state = customer?.homeAddress?.state;
    }
  }

  return car;
};

/**
 * Verify if renewal had any change on state or zip code
 * @param newState
 * @param newZipcode
 * @returns flag that indicated if state or zipcode was changed
 */
export const isCustomerStateChanged = async (
  newState: string | undefined,
  newZipcode: string | undefined
): Promise<boolean> => {
  const currentContract = getCurrentContract();
  const currentCustomer = await getCustomer();

  // Check currenct contract for renewal or default to current customer for all use cases
  const details = currentContract?.customer ?? currentCustomer;

  const isStateChanged =
    !isEmpty(newState) && details?.homeAddress?.state !== newState;
  const isZipChanged =
    !isEmpty(newZipcode) && details?.homeAddress?.zip !== newZipcode;

  return isStateChanged || isZipChanged;
};

/**
 * Clear customer address when state change
 */
export const clearAddressWhenStateChange = async (): Promise<void> => {
  const currentCustomer = _cache.getItem<CustomerModels.Customer>(
    _cache.Key.contact
  );
  const currentVehicle = _cache.getItem<VehicleModels.default>(
    _cache.Key.vehicle
  );
  const currentHomeAddress = {
    ...currentCustomer?.homeAddress,
    zip: currentVehicle?.zip ?? '',
    state: currentVehicle?.state ?? ''
  };

  // if state and zipcode was changed by the user, we reset the address
  if (await isCustomerStateChanged(currentVehicle?.state, currentVehicle?.zip)) {
    currentHomeAddress.address = '';
    currentHomeAddress.address2 = '';
    currentHomeAddress.city = '';
  } else {
    // update the address with the contract address if one exist
    // otherwise keep the data from the cache
    const details = getCurrentContract();
    const addressFromContract = details?.customer?.homeAddress;
    if (!isNil(addressFromContract)) {
      currentHomeAddress.address = addressFromContract.address || '';
      currentHomeAddress.address2 = addressFromContract.address2 || '';
      currentHomeAddress.city = addressFromContract.city || '';
    }
  }

  _cache.setItem<CustomerModels.Customer>(_cache.Key.contact, {
    ...currentCustomer,
    homeAddress: {
      ...currentHomeAddress
    }
  });
};

/**
 * merge vehicle to customer
 */
export const mergeVehicleToCustomer = async (): Promise<void> => {
  const currentCustomer = _cache.getItem<CustomerModels.Customer>(
    _cache.Key.contact
  );
  const currentVehicle = _cache.getItem<VehicleModels.default>(
    _cache.Key.vehicle
  );
  const carsList = currentCustomer?.cars ?? [];
  let newCarsList: VehicleModels.default[] = [];

  if (carsList.length > 0) {
    newCarsList = carsList.map((car) => {
      if (car.vin === currentVehicle.vin || car.id === undefined) {
        return { ...car, ...currentVehicle };
      }

      return car;
    });
  } else {
    newCarsList.push(currentVehicle);
  }

  _cache.setItem<CustomerModels.Customer>(_cache.Key.contact, {
    ...currentCustomer,
    vin: currentVehicle.vin,
    cars: newCarsList
  });
};

/**
 * merge vehicle from customer
 */
export const mergeVehicleFromCustomer = async (): Promise<void> => {
  const currentCustomer = _cache.getItem<CustomerModels.Customer>(
    _cache.Key.contact
  );
  const currentVehicle = _cache.getItem<VehicleModels.default>(
    _cache.Key.vehicle
  );
  let newVin = currentVehicle?.vin;
  let newSource = currentVehicle?.source;
  if (currentCustomer.vin !== newVin) {
    newVin = currentCustomer.vin ?? '';
    newSource = '';
  }
  _cache.setItem<VehicleModels.default>(_cache.Key.vehicle, {
    ...currentVehicle,
    zip: currentCustomer?.homeAddress?.zip ?? '',
    vin: newVin,
    source: newSource,
    miles: Number(currentCustomer?.mileage ?? 0),
    state: currentCustomer?.homeAddress?.state ?? '',
    useEstMileage: Number(currentCustomer?.mileage ?? 0) < 1 // 0g09162021 estimated/actual miles label
  });
};

/**
 * Updates cached vehicle with vin data from quotes.
 * @param vin Vin data.
 */
export const updateVehicleCache = (vin: QuotesModels.VIN): void => {
  try {
    const vehicle = _cache.getItem<VehicleModels.default>(_cache.Key.vehicle);

    const data = {
      ...vehicle,
      make: vin?.MakeDesc ?? vehicle?.make,
      model: vin?.ModelDesc ?? vehicle?.model,
      year: Number(vin?.Year ?? vehicle?.year),
      maxAClass: vin?.MaxAClass,
      maxBClass: vin?.MaxBClass,
      usedClass: vin?.UsedClass
    };

    _cache.setItem<VehicleModels.default>(_cache.Key.vehicle, data);
  } catch (error: unknown) {
    _log.error(error);
  }
};

/**
 * Updates cached vehicle with vin data from quotes.
 * @param vin Vin data.
 */
export const updateVehicleVin = (vin: string): void => {
  try {
    const vehicle = _cache.getItem<VehicleModels.default>(_cache.Key.vehicle);

    _cache.setItem<VehicleModels.default>(_cache.Key.vehicle, {
      ...vehicle,
      vin: vin
    });
  } catch (error: unknown) {
    _log.error(error);
  }
};

/**
 * Updates cached vehicle with current contract properties.
 * @param details Current Contract Details.
 */
export const updateVehicleCacheFromCurrentContract = async (
  details: ContractDetails | undefined = undefined
): Promise<void> => {
  details = details ?? getCurrentContract();
  const vehicle = _cache.getItem<VehicleModels.default>(_cache.Key.vehicle);
  const surcharges = await getPreRatingOptions(details?.plan?.surcharges);

  _cache.setItem<VehicleModels.default>(_cache.Key.vehicle, {
    ...vehicle,
    source: 'v',
    state: vehicle?.state ?? details?.customer?.homeAddress?.state,
    zip: vehicle?.zip ?? details?.customer?.homeAddress?.zip,
    ...details.vehicle,
    useEstMileage: true, // 0g09162021 estimated/actual miles label
    surcharges: surcharges
  });
};

/**
 * Gets minimum vehicle mileage acceptable if customer has a current contract.
 * @returns Minimum vehicle mileage.
 */
export const getMinVehicleMileage = (): number | undefined => {
  const currentContract = getCurrentContract();
  if (currentContract) return currentContract?.vehicle?.miles;
  return undefined;
};

/**
 * Resolves the estimated to actual mileage label
 * @returns the estimated or actual mileage label
 */
export const getDisplayMileage = (showEstimatedMiles?: boolean): string => {
  const car = getVehicle();
  let label = '';
  if (car) {
    const actualMiles = car.miles?.toLocaleString('en');
    const estMilesBand = showEstimatedMiles
      ? car.estCurrentMilesLabel
      : actualMiles;
    label = estMilesBand || '';
  }
  return label;
};

/**
 * Check to see if estimated or actual miles should be used
 * @returns true when using estimated miles
 */
export const useEstimatedMiles = async (): Promise<boolean> => {
  const car = getVehicle();
  return car?.useEstMileage ?? true;
};

/**
 * clear vehicle informations from customer
 */
export const removeVehicleCache = async (): Promise<void> => {
  _cache.removeItem(_cache.Key.vehicle);
};

/**
 * resets the vin information but maintains location data like state and zipcode
 * to allow the user interace to pre-select the information
 */
export const readyDataForNewVehicle = async (): Promise<void> => {
  const car = getVehicle();

  const newCar: VehicleModels.default = {
    state: car.state,
    zip: car.zip,
    vin: ''
  };

  _cache.setItem<VehicleModels.default>(_cache.Key.vehicle, newCar);
};

/**
 * Update vehicle data in cache with vin details.
 */
export const setVinDetails = async (): Promise<void> => {
  // Exit immediately if vin api is disabled.
  if (!isVinApiEnabled) return;

  try {
    // Retrieve vehicle info from cache.
    const vehicle = getVehicle();

    // Don't call vin details api if vehicle details
    // was already populated for the vin parameter.
    if (vehicle.vin === vehicle.apiFetchedVin) return;

    // Call vin detail function.
    const response = await getVinDetails(vehicle.vin);

    // Checks if function response succeeded.
    if (!response?.status) {
      _log.warn('Vin details API response returned false. Function finished.');
      return;
    }

    // Update vehicle cache with vin details.
    const vinDetails = response.result;
    const vehicleData = {
      ...vehicle,
      ...vinDetails,
      apiFetchedVin: vehicle.vin
    };
    _cache.setItem<VehicleModels.default>(_cache.Key.vehicle, vehicleData);
  } catch (error: unknown) {
    _log.error(error);
  }
};
